import {type EventBarDraftOrder, MAX_DRAFT_ORDERS} from '../hooks/useEventBarDraftOrders';
import {type EventBarSearchHistoryEntry} from '../hooks/useEventBarSearchHistory';

// The What panel blends draft orders and search history into one "Recent searches" section.
export type RecentItem =
  | {type: 'draft'; draftOrder: EventBarDraftOrder}
  | {type: 'history'; entry: EventBarSearchHistoryEntry};

// Collapse logically-identical past searches (same Where/When/Who + strategy + eventName) into one entry.
const historyDedupeKey = (entry: EventBarSearchHistoryEntry): string =>
  [
    entry.address.fullAddress,
    entry.eventOn ?? '',
    entry.eventLocalTime ?? '',
    entry.headcount ?? '',
    entry.strategy,
    entry.eventName ?? '',
  ].join('|');

// The identity of a search ignoring its date/time — used to drop a date/time-less search when an
// otherwise-identical dated sibling exists (same Where/Who + strategy + eventName).
const historyBaseKey = (entry: EventBarSearchHistoryEntry): string =>
  [entry.address.fullAddress, entry.headcount ?? '', entry.strategy, entry.eventName ?? ''].join(
    '|',
  );

const hasDateTime = (entry: EventBarSearchHistoryEntry): boolean =>
  !!(entry.eventOn || entry.eventLocalTime);

/**
 * Merges draft orders and search history into a single, recency-ordered "Recent searches" list.
 *
 * Both sources come from the same `identity.fulfillmentDetails` connection, and search history is the
 * unfiltered superset; recency wins, so a recent past search can outrank an older draft and vice versa.
 * Draft orders whose fulfillment detail falls outside the history window (older, but still an active cart)
 * are appended after, preserving their own cart-recency order. Capped at `max`.
 *
 * History rows are deduped two ways: exact duplicates (same Where/When/Who + strategy + eventName) collapse
 * to the first (most recent) occurrence, and a date/time-less search is dropped when an otherwise-identical
 * dated sibling exists. Drafts are never collapsed this way.
 */
export const mergeRecentItems = (
  draftOrders: EventBarDraftOrder[],
  searchHistory: EventBarSearchHistoryEntry[],
  max = MAX_DRAFT_ORDERS,
): RecentItem[] => {
  const draftsByFdId = new Map(
    draftOrders.map(draftOrder => [draftOrder.fulfillmentDetailId, draftOrder] as const),
  );
  const items: RecentItem[] = [];
  const seenHistoryKeys = new Set<string>();

  // Base keys (ignoring date/time) that have at least one dated search — a date/time-less search
  // sharing one of these is redundant and gets dropped.
  const datedBaseKeys = new Set(searchHistory.filter(hasDateTime).map(historyBaseKey));

  for (const entry of searchHistory) {
    const draftOrder = draftsByFdId.get(entry.fulfillmentDetailId);
    if (draftOrder) {
      items.push({type: 'draft', draftOrder});
      draftsByFdId.delete(entry.fulfillmentDetailId);
    } else {
      // Drop a date/time-less search when an otherwise-identical dated sibling exists.
      if (!hasDateTime(entry) && datedBaseKeys.has(historyBaseKey(entry))) continue;
      const key = historyDedupeKey(entry);
      if (seenHistoryKeys.has(key)) continue;
      seenHistoryKeys.add(key);
      items.push({type: 'history', entry});
    }
  }

  // Any draft whose fulfillment detail wasn't in the history window — keep it available after the
  // history-ordered items, in its original (cart-recency) order.
  for (const draftOrder of draftOrders) {
    if (draftsByFdId.has(draftOrder.fulfillmentDetailId)) {
      items.push({type: 'draft', draftOrder});
    }
  }

  return items.slice(0, max);
};
